import clsx from 'clsx'
import { FirebaseError } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import React, { useRef, useState } from 'react'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import app from '~/src/lib/firebase/firebaseApp'
import {
  isValidEmail,
  sendPasswordResetEmail,
  signinWithEmail,
} from '~/src/lib/firebase/firebaseAuth'
import logger from '~/src/lib/logger'

interface EmailLoginFormProps {
  clearPendingEmailAuth?: () => void
}

const auth = getAuth(app)

const EmailLoginForm: React.FC<EmailLoginFormProps> = ({
  clearPendingEmailAuth,
}) => {
  const passwordRef = useRef<HTMLInputElement>(null)
  const [emailInput, setEmailInput] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordResetEnabled, setPasswordResetEnabled] = useState(false)
  const [processingEmailAuth, setProcessingEmailAuth] = useState(false)
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value)
  }
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordInput(e.target.value)
  }

  const loginWithEmail = async () => {
    setProcessingEmailAuth(true)
    setErrorMsg(null)
    try {
      // validate email
      if (!isValidEmail(emailInput)) {
        setErrorMsg('Invalid email address, please try again')
        setProcessingEmailAuth(false)
        return
      }

      // check password length
      if (passwordInput.length < 6) {
        setErrorMsg('Password must be at least 6 characters')
        setProcessingEmailAuth(false)
        return
      }

      await signinWithEmail({ email: emailInput, password: passwordInput })
    } catch (error) {
      const fbError = error as FirebaseError
      logger.debug('[signinWithEmail] error', { error: fbError })
      setErrorMsg(fbError.message)
    }

    setProcessingEmailAuth(false)
  }

  const resetPassword = async () => {
    setProcessingEmailAuth(true)
    setErrorMsg(null)

    // first check email is valid
    if (!emailInput) {
      setErrorMsg('Email is required')
      setProcessingEmailAuth(false)
      return
    }

    if (!isValidEmail(emailInput)) {
      setErrorMsg('Invalid email address')
      setProcessingEmailAuth(false)
      return
    }

    try {
      // send password reset email
      await sendPasswordResetEmail(auth, emailInput)
      alert('Sending password reset email if account exists')
      setPasswordResetEnabled(false)
    } catch (error) {
      const fbError = error as FirebaseError
      logger.debug('[resetPassword] error', { error: fbError })
      setErrorMsg(fbError.message)
    }

    setProcessingEmailAuth(false)
  }

  return (
    <>
      <button
        className='mx-auto mb-4 rounded-lg px-6 py-2 text-sm underline opacity-50'
        onClick={clearPendingEmailAuth}
      >
        Go back
      </button>
      <div className='flex w-full flex-col items-center justify-center space-y-2'>
        <input
          type='email'
          value={emailInput}
          onChange={handleEmailChange}
          className='flex w-full flex-row rounded-full border-2 border-black px-4 py-2 pl-4 text-lg'
          placeholder='Email address'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // switch focus to password input
              if (passwordResetEnabled) {
                resetPassword()
              } else {
                passwordRef.current?.focus()
              }
            }
          }}
        />
        <div
          className={clsx(
            'flex w-full flex-row justify-between overflow-hidden rounded-full border-2 border-black',
            {
              hidden: passwordResetEnabled,
            }
          )}
        >
          <input
            type='password'
            ref={passwordRef}
            value={passwordInput}
            onChange={handlePasswordChange}
            className='w-full rounded-full rounded-r-none py-2 pl-4 text-lg outline-none focus-within:outline-none'
            placeholder='Password'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                loginWithEmail()
              }
            }}
          />
          <button
            className='flex items-center justify-center bg-blue-500 px-4 font-semibold text-white hover:bg-blue-700'
            onClick={loginWithEmail}
          >
            {processingEmailAuth ? (
              <div className='w-[20px] animate-spin'>
                <LoaderIcon width={18} height={18} />
              </div>
            ) : (
              'Submit'
            )}
          </button>
        </div>

        {passwordResetEnabled ? (
          <button
            className='flex h-[52px] w-full items-center justify-center rounded bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-700'
            onClick={resetPassword}
          >
            {processingEmailAuth ? (
              <div className='w-[20px] animate-spin'>
                <LoaderIcon width={18} height={18} />
              </div>
            ) : (
              'Submit'
            )}
          </button>
        ) : (
          <div>
            <button
              onClick={() => setPasswordResetEnabled(true)}
              className='text-xs font-semibold text-blue-500'
            >
              Forgot your password?
            </button>
          </div>
        )}

        {errorMsg && (
          <div className='text-sm font-semibold text-red-500'>{errorMsg}</div>
        )}
      </div>
    </>
  )
}

export default EmailLoginForm
